import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createOrganizationSchema,
  createWebsiteSchema,
} from "../../seo/utils";
import blogsData from "../../data/blogs.json";

interface Blog {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  image: string;
  publishedOn: string;
  readingTime: number;
  body: string;
  cta: string;
  targetKeyword: string;
}

const formatArticleDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

function BlogsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [hiddenImages, setHiddenImages] = useState<Record<number, boolean>>({});

  const blogs: Blog[] = blogsData.blogs;

  const categories = useMemo(
    () =>
      blogs.reduce<string[]>((uniqueCategories, blog) => {
        if (!uniqueCategories.includes(blog.category)) {
          uniqueCategories.push(blog.category);
        }

        return uniqueCategories;
      }, []),
    [blogs]
  );

  const filteredBlogs = useMemo(
    () =>
      blogs.filter((blog) => {
        const matchesSearch =
          !searchQuery ||
          [blog.title, blog.description, ...blog.tags].some((text) =>
            text.toLowerCase().includes(searchQuery.toLowerCase())
          );

        const matchesCategory =
          !selectedCategory || blog.category === selectedCategory;

        return matchesSearch && matchesCategory;
      }),
    [blogs, searchQuery, selectedCategory]
  );

  const heroBlog = filteredBlogs[0];
  const sideBlogs = filteredBlogs.slice(1, 5);
  const popularBlogs = filteredBlogs.slice(1);

  const readingMoments = useMemo(
    () =>
      filteredBlogs.reduce(
        (minutes, blog) => minutes + blog.readingTime,
        0
      ),
    [filteredBlogs]
  );

  const lastPublishedDate = useMemo(() => {
    if (!filteredBlogs.length) {
      return "";
    }

    const latestBlog = [...filteredBlogs].sort(
      (leftBlog, rightBlog) =>
        new Date(rightBlog.publishedOn).getTime() -
        new Date(leftBlog.publishedOn).getTime()
    )[0];

    return formatArticleDate(latestBlog.publishedOn);
  }, [filteredBlogs]);

  const schema = [
    createOrganizationSchema(),
    createWebsiteSchema(),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blogs" },
    ]),
  ];

  return (
    <>
      <Seo
        title="Blog - ShopKaBot | AI Automation Insights"
        description="Read expert guides on WhatsApp business automation, AI chatbots, customer support, sales automation, and more."
        path="/blogs"
        keywords={[
          "blog",
          "AI automation",
          "WhatsApp business",
          "chatbot",
          "customer support",
        ]}
        schema={schema}
        type="website"
      />

      <Box
        sx={{
          background:
            "linear-gradient(140deg, #0f766e 0%, #1d4ed8 45%, #2563eb 100%)",
          px: { xs: 1.5, md: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Paper
            elevation={0}
            sx={{
              overflow: "hidden",
              borderRadius: { xs: 4, md: 6 },
              border: "1px solid rgba(255, 255, 255, 0.36)",
              boxShadow: "0 30px 90px rgba(15, 23, 42, 0.22)",
              bgcolor: "#f8fbfb",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 4.5 },
                pt: { xs: 4, md: 5 },
                pb: { xs: 3, md: 4 },
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(247, 251, 251, 0.98) 100%)",
              }}
            >
              <Stack spacing={2.5} alignItems="center" textAlign="center">
                <Chip
                  label="ShopKaBot Editorial"
                  sx={{
                    borderRadius: "999px",
                    bgcolor: alpha("#10b981", 0.1),
                    color: "#047857",
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                />

                <Typography
                  component="h1"
                  sx={{
                    maxWidth: 760,
                    fontSize: { xs: "2rem", md: "3.2rem" },
                    lineHeight: { xs: 1.08, md: 1.02 },
                    letterSpacing: "-0.045em",
                    fontWeight: 800,
                    color: "#1f2a44",
                  }}
                >
                  Read our latest automation blogs
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 760,
                    color: "#6b7280",
                    lineHeight: 1.8,
                    fontSize: { xs: "0.98rem", md: "1.05rem" },
                  }}
                >
                  Discover sharp ideas, implementation notes, and practical
                  guidance for building better WhatsApp sales, support, and
                  booking workflows.
                </Typography>

                <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap justifyContent="center">
                  {[
                    `${filteredBlogs.length} posts`,
                    `${readingMoments} minutes of reading`,
                    lastPublishedDate ? `Updated ${lastPublishedDate}` : null,
                  ]
                    .filter(Boolean)
                    .map((item) => (
                      <Box
                        key={item}
                        sx={{
                          px: 1.5,
                          py: 0.9,
                          borderRadius: "999px",
                          bgcolor: "#ffffff",
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          color: "#475569",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                </Stack>

                <TextField
                  placeholder="Search blog articles"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  sx={{
                    width: "100%",
                    maxWidth: 420,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "999px",
                      bgcolor: "#ffffff",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.04)",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    pt: 1.5,
                    borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <Chip
                    label="All"
                    clickable
                    onClick={() => setSelectedCategory(null)}
                    sx={{
                      borderRadius: "999px",
                      bgcolor:
                        selectedCategory === null ? "#16a34a" : "transparent",
                      color:
                        selectedCategory === null ? "#ffffff" : "#64748b",
                      border:
                        selectedCategory === null
                          ? "1px solid #16a34a"
                          : "1px solid transparent",
                      fontWeight: 700,
                    }}
                  />
                  {categories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      clickable
                      onClick={() => setSelectedCategory(category)}
                      sx={{
                        borderRadius: "999px",
                        bgcolor:
                          selectedCategory === category
                            ? "#16a34a"
                            : "transparent",
                        color:
                          selectedCategory === category ? "#ffffff" : "#64748b",
                        border:
                          selectedCategory === category
                            ? "1px solid #16a34a"
                            : "1px solid transparent",
                        fontWeight: 700,
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4.5 }, pb: { xs: 4, md: 5 } }}>
              {heroBlog ? (
                <>
                  <Grid
                    container
                    spacing={{ xs: 2.5, md: 3 }}
                    sx={{ mb: { xs: 4, md: 5 } }}
                  >
                    <Grid size={{ xs: 12, lg: 7.8 }}>
                      <Card
                        sx={{
                          borderRadius: 4,
                          overflow: "hidden",
                          bgcolor: "#ffffff",
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        {heroBlog.image && !hiddenImages[heroBlog.id] ? (
                          <CardMedia
                            component="img"
                            image={heroBlog.image}
                            alt={heroBlog.title}
                            onError={() =>
                              setHiddenImages((currentImages) => ({
                                ...currentImages,
                                [heroBlog.id]: true,
                              }))
                            }
                            sx={{
                              height: { xs: 240, md: 400 },
                              objectFit: "cover",
                            }}
                          />
                        ) : null}

                        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 1, gap: 1, flexWrap: "wrap" }}
                          >
                            <Typography
                              sx={{
                                color: "#16a34a",
                                fontSize: "0.82rem",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                              }}
                            >
                              {heroBlog.category}
                            </Typography>
                            <Typography
                              sx={{ color: "#94a3b8", fontSize: "0.88rem" }}
                            >
                              Featured article
                            </Typography>
                          </Stack>

                          <Typography
                            sx={{
                              fontSize: { xs: "1.35rem", md: "1.85rem" },
                              lineHeight: 1.18,
                              fontWeight: 800,
                              color: "#1f2937",
                              mb: 1.25,
                            }}
                          >
                            {heroBlog.title}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 1.5, color: "#94a3b8" }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <CalendarMonthIcon sx={{ fontSize: 16 }} />
                              <Typography variant="body2">
                                {formatArticleDate(heroBlog.publishedOn)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <AccessTimeIcon sx={{ fontSize: 16 }} />
                              <Typography variant="body2">
                                {heroBlog.readingTime} min read
                              </Typography>
                            </Stack>
                          </Stack>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              lineHeight: 1.85,
                              mb: 2.25,
                              maxWidth: 780,
                            }}
                          >
                            {heroBlog.description}
                          </Typography>

                          <Button
                            endIcon={<ArrowOutwardIcon />}
                            onClick={() => navigate(`/blogs/${heroBlog.slug}`)}
                            sx={{
                              px: 0,
                              color: "#16a34a",
                              fontWeight: 800,
                              "&:hover": {
                                bgcolor: "transparent",
                                color: "#15803d",
                              },
                            }}
                          >
                            Read more
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 4.2 }}>
                      <Stack spacing={2}>
                        {sideBlogs.map((blog) => (
                          <Card
                            key={blog.id}
                            sx={{
                              borderRadius: 3.5,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              bgcolor: "#ffffff",
                              boxShadow: "0 16px 35px rgba(15, 23, 42, 0.05)",
                            }}
                          >
                            <Stack direction="row" spacing={1.5} sx={{ p: 1.5 }}>
                              {blog.image && !hiddenImages[blog.id] ? (
                                <CardMedia
                                  component="img"
                                  image={blog.image}
                                  alt={blog.title}
                                  onError={() =>
                                    setHiddenImages((currentImages) => ({
                                      ...currentImages,
                                      [blog.id]: true,
                                    }))
                                  }
                                  sx={{
                                    width: 104,
                                    minWidth: 104,
                                    height: 92,
                                    borderRadius: 2.5,
                                    objectFit: "cover",
                                  }}
                                />
                              ) : null}

                              <Box sx={{ minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ mb: 0.75, gap: 1 }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#16a34a",
                                      fontSize: "0.74rem",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {blog.category}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#94a3b8",
                                      fontSize: "0.75rem",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {blog.readingTime} min
                                  </Typography>
                                </Stack>

                                <Typography
                                  sx={{
                                    color: "#1f2937",
                                    fontWeight: 800,
                                    fontSize: "0.98rem",
                                    lineHeight: 1.35,
                                    mb: 0.75,
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {blog.title}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: "#94a3b8",
                                    fontSize: "0.78rem",
                                    mb: 1,
                                  }}
                                >
                                  {formatArticleDate(blog.publishedOn)}
                                </Typography>

                                <Button
                                  size="small"
                                  onClick={() => navigate(`/blogs/${blog.slug}`)}
                                  sx={{
                                    minWidth: 0,
                                    px: 0,
                                    color: "#16a34a",
                                    fontWeight: 800,
                                  }}
                                >
                                  Open
                                </Button>
                              </Box>
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: { xs: 3, md: 4 } }} />

                  <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.35rem", md: "1.55rem" },
                        fontWeight: 800,
                        color: "#1f2a44",
                        mb: 2.5,
                      }}
                    >
                      Popular posts
                    </Typography>

                    <Grid container spacing={{ xs: 2, md: 2.5 }}>
                      {popularBlogs.length > 0
                        ? popularBlogs.map((blog) => (
                            <Grid size={{ xs: 12, md: 6, xl: 4 }} key={blog.id}>
                              <Card
                                sx={{
                                  height: "100%",
                                  borderRadius: 3.5,
                                  border: "1px solid rgba(15, 23, 42, 0.08)",
                                  bgcolor: "#ffffff",
                                  boxShadow: "0 16px 35px rgba(15, 23, 42, 0.05)",
                                  transition:
                                    "transform 0.22s ease, box-shadow 0.22s ease",
                                  "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow:
                                      "0 22px 45px rgba(15, 23, 42, 0.08)",
                                  },
                                }}
                              >
                                {blog.image && !hiddenImages[blog.id] ? (
                                  <CardMedia
                                    component="img"
                                    image={blog.image}
                                    alt={blog.title}
                                    onError={() =>
                                      setHiddenImages((currentImages) => ({
                                        ...currentImages,
                                        [blog.id]: true,
                                      }))
                                    }
                                    sx={{
                                      height: 200,
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : null}

                                <CardContent sx={{ p: 2.25 }}>
                                  <Typography
                                    sx={{
                                      color: "#1f2937",
                                      fontWeight: 800,
                                      fontSize: "1.05rem",
                                      lineHeight: 1.4,
                                      mb: 1,
                                    }}
                                  >
                                    {blog.title}
                                  </Typography>

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                      mb: 1.25,
                                      color: "#94a3b8",
                                      fontSize: "0.82rem",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography variant="caption">
                                      {formatArticleDate(blog.publishedOn)}
                                    </Typography>
                                    <Typography variant="caption">
                                      {blog.readingTime} min read
                                    </Typography>
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    sx={{ mb: 1.25, color: "#16a34a" }}
                                  >
                                    <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                                    <Typography
                                      sx={{ fontSize: "0.78rem", fontWeight: 700 }}
                                    >
                                      {blog.category}
                                    </Typography>
                                  </Stack>

                                  <Typography
                                    sx={{
                                      color: "#6b7280",
                                      lineHeight: 1.8,
                                      fontSize: "0.94rem",
                                      mb: 1.5,
                                    }}
                                  >
                                    {blog.description}
                                  </Typography>

                                  <Button
                                    size="small"
                                    endIcon={<ArrowOutwardIcon />}
                                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                                    sx={{
                                      px: 0,
                                      color: "#16a34a",
                                      fontWeight: 800,
                                      "&:hover": { bgcolor: "transparent" },
                                    }}
                                  >
                                    Read more
                                  </Button>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))
                        : null}
                    </Grid>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: { xs: 4, md: 5 } }}
                  >
                    {["First", "01", "02", "03", "Last"].map((item, index) => (
                      <Box
                        key={item}
                        sx={{
                          minWidth: 52,
                          px: 1.75,
                          py: 1.1,
                          borderRadius: 2.5,
                          textAlign: "center",
                          bgcolor: index === 1 ? "#ffffff" : "transparent",
                          color: index === 1 ? "#16a34a" : "#64748b",
                          fontWeight: 700,
                          border: "1px solid rgba(15, 23, 42, 0.12)",
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      bgcolor: "#083344",
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 2.25, md: 4.5 },
                        py: { xs: 4, md: 5 },
                        textAlign: "center",
                        color: "#ffffff",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "1.85rem", md: "2.4rem" },
                          fontWeight: 800,
                          lineHeight: 1.08,
                          mb: 1.25,
                        }}
                      >
                        Build smarter WhatsApp growth systems
                      </Typography>
                      <Typography
                        sx={{
                          maxWidth: 760,
                          mx: "auto",
                          color: "#cbd5e1",
                          lineHeight: 1.8,
                          mb: 4,
                        }}
                      >
                        Turn article ideas into real operating leverage with
                        conversion-focused automation, better lead capture, and
                        cleaner customer journeys.
                      </Typography>

                      <Grid container spacing={2.5}>
                        {[
                          {
                            icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
                            title: "Playbook Review",
                            description:
                              "We review your current flow and identify the fastest automation wins.",
                          },
                          {
                            icon: <RuleFolderOutlinedIcon sx={{ fontSize: 28 }} />,
                            title: "Workflow Planning",
                            description:
                              "Map support, lead capture, and booking automation around your business model.",
                          },
                          {
                            icon: <TravelExploreOutlinedIcon sx={{ fontSize: 28 }} />,
                            title: "Execution Support",
                            description:
                              "Move from concept to implementation with a tailored rollout path.",
                          },
                        ].map((item) => (
                          <Grid size={{ xs: 12, md: 4 }} key={item.title}>
                            <Paper
                              elevation={0}
                              sx={{
                                height: "100%",
                                p: 3,
                                borderRadius: 3.5,
                                textAlign: "left",
                                bgcolor: "#ffffff",
                                color: "#1f2937",
                              }}
                            >
                              <Box sx={{ color: "#16a34a", mb: 2 }}>
                                {item.icon}
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "1.08rem",
                                  mb: 1,
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                sx={{ color: "#6b7280", lineHeight: 1.8, mb: 2 }}
                              >
                                {item.description}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => navigate("/contact-us")}
                                sx={{
                                  px: 0,
                                  color: "#16a34a",
                                  fontWeight: 800,
                                }}
                              >
                                More details
                              </Button>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                </>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: "1px dashed rgba(15, 23, 42, 0.16)",
                    bgcolor: "#ffffff",
                    p: { xs: 3, md: 4 },
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.4rem",
                      fontWeight: 800,
                      color: "#1f2937",
                      mb: 1,
                    }}
                  >
                    No blog articles match this filter.
                  </Typography>
                  <Typography
                    sx={{ color: "#6b7280", lineHeight: 1.8, mb: 2.5 }}
                  >
                    Try another keyword or switch back to all categories to see
                    the full journal.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory(null);
                    }}
                    sx={{
                      borderRadius: "999px",
                      bgcolor: "#16a34a",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "#15803d" },
                    }}
                  >
                    Reset filters
                  </Button>
                </Paper>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}

export default BlogsPage;
